import { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { useQuery, useQueryClient } from 'react-query'
import { addDays, format, getDay, getDate, getMonth, getYear, isWeekend, startOfDay, startOfMonth, endOfMonth, isAfter, isSameDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useUser } from '../../../hooks/useUser'

export const useWorkdayCalendar = () => {
  const { codigo_usuario } = useUser()
  const isAdminUser = true

  const realDate = useMemo(() => new Date(), [])

  const [isSimulationMode, setIsSimulationMode] = useState(false)
  const [simulatedDate, setSimulatedDate] = useState(null)

  const TODAY = useMemo(() => (isSimulationMode && simulatedDate ? simulatedDate : realDate), [isSimulationMode, simulatedDate, realDate])

  const [workdays, setWorkdays] = useState([])
  const [currentWorkday, setCurrentWorkday] = useState(null)
  const [calendarDays, setCalendarDays] = useState([])
  const [calendarMonth, setCalendarMonth] = useState(getMonth(TODAY))
  const [calendarYear, setCalendarYear] = useState(getYear(TODAY))
  const queryClient = useQueryClient()

  const holidayCache = useMemo(() => new Map(), [])

  const fetchHolidaysForYear = useCallback(
    async (year) => {
      if (holidayCache.has(year)) {
        return holidayCache.get(year)
      }

      try {
        const { data } = await axios.get(`https://brasilapi.com.br/api/feriados/v1/${year}`)

        const holidayMap = {}
        data.forEach((holiday) => {
          holidayMap[holiday.date] = true
        })

        holidayCache.set(year, holidayMap)
        return holidayMap
      } catch (error) {
        console.error('Erro ao buscar feriados:', error)
        return {}
      }
    },
    [holidayCache]
  )

  const useHolidays = (year) => {
    return useQuery({
      queryKey: ['holidays', year],
      queryFn: () => fetchHolidaysForYear(year),
      staleTime: 1000 * 60 * 60 * 24,
      cacheTime: 1000 * 60 * 60 * 24 * 30,
      retry: 1,
    })
  }

  useEffect(() => {
    const currentYear = getYear(TODAY)
    queryClient.prefetchQuery(['holidays', currentYear], () => fetchHolidaysForYear(currentYear))
    queryClient.prefetchQuery(['holidays', currentYear + 1], () => fetchHolidaysForYear(currentYear + 1))
  }, [queryClient, TODAY, fetchHolidaysForYear])

  const isHoliday = useCallback(
    (date) => {
      const year = getYear(date)
      const month = String(getMonth(date) + 1).padStart(2, '0')
      const day = String(getDate(date)).padStart(2, '0')
      const dateStr = `${year}-${month}-${day}`

      const holidaysData = queryClient.getQueryData(['holidays', year])
      return holidaysData ? !!holidaysData[dateStr] : false
    },
    [queryClient]
  )

  const isWorkday = useCallback(
    (date) => {
      if (isWeekend(date)) return false
      return !isHoliday(date)
    },
    [isHoliday]
  )

  const findNextWorkday = useCallback(
    (currentDate) => {
      let nextDate = addDays(currentDate, 1)

      while (!isWorkday(nextDate)) {
        nextDate = addDays(nextDate, 1)
      }

      return nextDate
    },
    [isWorkday]
  )

  const calculateWorkdays = useCallback(() => {
    const now = TODAY
    const currentHour = now.getHours()
    const currentMinutes = now.getMinutes()
    const isAfterWorkHours = currentHour > 14 || (currentHour === 14 && currentMinutes > 0)

    let firstWorkday
    if (isWorkday(now)) {
      if (isAfterWorkHours) {
        const nextWorkday = findNextWorkday(now)
        firstWorkday = findNextWorkday(nextWorkday)
      } else {
        firstWorkday = findNextWorkday(now)
      }
    } else {
      firstWorkday = findNextWorkday(now)
    }

    const workdaysList = []
    let currentDate = firstWorkday

    while (workdaysList.length < 10) {
      workdaysList.push(new Date(currentDate))
      currentDate = findNextWorkday(currentDate)
    }

    return workdaysList
  }, [TODAY, isWorkday, findNextWorkday])

  const currentYear = getYear(TODAY)
  const { status: holidaysStatus, data: holidaysData } = useHolidays(currentYear)

  const determineCurrentWorkday = useCallback(
    (workdays) => {
      if (!workdays.length) return null

      const today = startOfDay(TODAY)
      const hour = TODAY.getHours()
      const minutes = TODAY.getMinutes()

      for (let i = 0; i < workdays.length; i++) {
        const workday = startOfDay(workdays[i])

        if (isSameDay(workday, today)) {
          if (i === 0 && (hour > 14 || (hour === 14 && minutes > 0))) {
            return i + 1 < workdays.length ? i + 1 : null
          }
          return i
        } else if (isAfter(workday, today)) {
          return i > 0 ? i - 1 : null
        }
      }

      return workdays.length - 1
    },
    [TODAY]
  )

  const [isChangingFromSimulation, setIsChangingFromSimulation] = useState(false)

  const navigateToPreviousMonth = useCallback(() => {
    setCalendarMonth((prevMonth) => {
      if (prevMonth === 0) {
        setCalendarYear((prevYear) => prevYear - 1)
        return 11
      }
      return prevMonth - 1
    })
  }, [])

  const navigateToNextMonth = useCallback(() => {
    setCalendarMonth((prevMonth) => {
      if (prevMonth === 11) {
        setCalendarYear((prevYear) => prevYear + 1)
        return 0
      }
      return prevMonth + 1
    })
  }, [])

  const generateCalendarGrid = useCallback(
    (workdays) => {
      if (!workdays.length) return []

      const workdayMap = new Map()
      workdays.forEach((workday, index) => {
        const key = format(workday, 'yyyy-MM-dd')
        workdayMap.set(key, index)
      })

      const firstDayOfMonth = startOfMonth(new Date(calendarYear, calendarMonth))
      const firstDayOfWeek = getDay(firstDayOfMonth)

      const lastDayOfMonth = endOfMonth(new Date(calendarYear, calendarMonth))
      const lastDate = getDate(lastDayOfMonth)

      const calendarDays = []

      if (firstDayOfWeek > 0) {
        for (let i = 0; i < firstDayOfWeek; i++) {
          const date = addDays(firstDayOfMonth, -firstDayOfWeek + i)
          const key = format(date, 'yyyy-MM-dd')
          const workdayIndex = workdayMap.get(key)

          calendarDays.push({
            date,
            workdayIndex: workdayIndex !== undefined ? workdayIndex : null,
            isCurrentMonth: false,
          })
        }
      }

      for (let day = 1; day <= lastDate; day++) {
        const date = new Date(calendarYear, calendarMonth, day)
        const key = format(date, 'yyyy-MM-dd')
        const workdayIndex = workdayMap.get(key)

        calendarDays.push({
          date,
          workdayIndex: workdayIndex !== undefined ? workdayIndex : null,
          isCurrentMonth: true,
        })
      }

      const totalCells = 42
      const remainingCells = totalCells - calendarDays.length

      if (remainingCells > 0) {
        for (let i = 1; i <= remainingCells; i++) {
          const date = addDays(lastDayOfMonth, i)
          const key = format(date, 'yyyy-MM-dd')
          const workdayIndex = workdayMap.get(key)

          calendarDays.push({
            date,
            workdayIndex: workdayIndex !== undefined ? workdayIndex : null,
            isCurrentMonth: false,
          })
        }
      }

      return calendarDays
    },
    [calendarMonth, calendarYear]
  )

  const formatMonthYear = useCallback((date) => {
    if (!date) return ''
    return format(date, 'MMMM yyyy', { locale: ptBR })
  }, [])

  useEffect(() => {
    if (holidaysStatus === 'success') {
      const days = calculateWorkdays()
      setWorkdays(days)

      if (days.length > 0) {
        setCalendarMonth(getMonth(days[0]))
        setCalendarYear(getYear(days[0]))
      }

      const currentDay = determineCurrentWorkday(days)
      setCurrentWorkday(currentDay)
    }
  }, [holidaysStatus, calculateWorkdays, determineCurrentWorkday])

  useEffect(() => {
    if (workdays.length > 0) {
      setCalendarDays(generateCalendarGrid(workdays))
    }
  }, [workdays, calendarMonth, calendarYear, generateCalendarGrid])

  const updateSimulatedDate = useCallback(
    (dateString, timeString) => {
      if (!isAdminUser) return

      try {
        const [year, month, day] = dateString.split('-').map(Number)
        const [hours, minutes] = timeString?.split(':').map(Number) || [0, 0]

        setIsChangingFromSimulation(true)

        const newDate = new Date(year, month - 1, day, hours, minutes)
        setSimulatedDate(newDate)

        setIsSimulationMode(true)

        setCalendarMonth(month - 1)
        setCalendarYear(year)

        setTimeout(() => {
          setIsChangingFromSimulation(false)
        }, 100)
      } catch (error) {
        console.error('Erro ao atualizar data simulada:', error)
        setIsChangingFromSimulation(false)
      }
    },
    [isAdminUser]
  )

  const resetSimulation = useCallback(() => {
    setIsSimulationMode(false)
    setSimulatedDate(null)

    setCalendarMonth(getMonth(realDate))
    setCalendarYear(getYear(realDate))
  }, [realDate])

  return {
    workdays,
    currentWorkday,
    calendarDays,
    formatMonthYear,
    isLoading: holidaysStatus === 'loading',
    simulatedDate: TODAY,
    navigateToPreviousMonth,
    navigateToNextMonth,
    calendarMonth,
    calendarYear,
    isAdminUser,
    updateSimulatedDate,
    resetSimulation,
    isSimulated: isSimulationMode,
  }
}
